import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useRef } from "react";
import type { BaseScene } from "./BaseScene.js";
import { useBase } from "./state.js";
import { Timer } from "../ui/Timer.js";
import { useT } from "../i18n/index.js";
import { useColony } from "./colony-state.js";
import { formatCompact } from "../ui/Counter.js";
import { ResourceIcon } from "../assets/gameIcons.js";
import { buildingName } from "./panels/shared.js";
import { placeLabel } from "./labelPlacement.js";

/**
 * HTML labels projected over building anchors: level badge, running timer, upgrade-ready mark and zone locks.
 * Positions are written straight to the DOM every frame; React only re-renders when the state changes (R10).
 */
export function Labels({ scene }: { scene: BaseScene | null }) {
  const t = useT();
  const base = useBase((s) => s.base);
  const select = useBase((s) => s.select);
  const pendingRows = useColony(s => s.pendingRows);
  const refs = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    if (!scene) return;
    let raf = 0;
    const sizes = new Map<Element, { width: number; height: number }>();
    let bounds = { left: 8, right: innerWidth - 108, top: 176, bottom: innerHeight - 188 };
    const updateBounds = () => {
      const dock = document.querySelector(".reward-dock")?.getBoundingClientRect();
      const controls = document.querySelector(".base-control-strip")?.getBoundingClientRect();
      bounds = { left: innerWidth >= 900 ? 108 : 8, right: dock ? dock.left - 10 : innerWidth - 12,
        top: (controls?.bottom ?? 166) + 10, bottom: innerHeight - (innerWidth >= 900 ? 92 : 192) };
    };
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) sizes.set(entry.target, { width: entry.target.clientWidth, height: entry.target.clientHeight });
      updateBounds();
    });
    for (const element of refs.current.values()) observer.observe(element);
    for (const element of document.querySelectorAll(".reward-dock,.base-control-strip")) observer.observe(element);
    updateBounds();
    window.addEventListener("resize", updateBounds);
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const occupied: { left: number; top: number; right: number; bottom: number }[] = [];
      for (const a of scene.anchors()) {
        const el = refs.current.get(a.rowId);
        if (!el) continue;
        const producer = base?.loop?.producers.some(row => row.rowId === a.rowId && row.pending > 0);
        const size = sizes.get(el) ?? (producer ? { width: 92, height: 82 } : { width: 36, height: 30 });
        const rect = a.visible ? placeLabel(a.x, a.y, size.width, size.height, bounds, occupied) : null;
        if (rect) { occupied.push(rect); el.style.transform = `translate(${rect.left.toFixed(1)}px, ${rect.top.toFixed(1)}px)`; }
        el.style.opacity = rect ? "1" : "0";
        el.style.visibility = rect ? "visible" : "hidden";
        el.style.zIndex = String((producer ? 1000 : 0) + Math.round((1 - a.depth) * 500));
      }
      if (base) {
        for (const z of base.zones) {
          const el = refs.current.get(`zone:${z.id}`);
          if (!el) continue;
          const a = scene.projectCell(z.rect.x, z.rect.y, z.rect.w, z.rect.h, 0.6);
          el.style.transform = `translate(-50%, -50%) translate(${a.x.toFixed(1)}px, ${a.y.toFixed(1)}px)`;
          el.style.opacity = a.visible ? "1" : "0";
          el.style.visibility = a.visible ? "visible" : "hidden";
        }
      }
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); observer.disconnect(); window.removeEventListener("resize", updateBounds); };
  }, [scene, base]);

  if (!base) return null;
  const setRef = (key: string) => (el: HTMLDivElement | null) => {
    if (el) refs.current.set(key, el);
    else refs.current.delete(key);
  };
  return (
    <div className="labels" aria-hidden={false}>
      {base.buildings
        .filter((b) => b.w > 0)
        .map((b) => (
          <div key={b.id} ref={setRef(b.id)} className="label" data-testid={`label-${b.buildingId}`}>
            {base.loop?.producers.filter((row) => row.rowId === b.id && row.pending > 0).map((row) => <button key={row.rowId} type="button" className={`resource-bubble ${row.bubbleReady ? "ready" : ""}`} disabled={pendingRows.includes(row.rowId) || pendingRows.includes("all")} data-testid={`bubble-${row.rowId}`} aria-label={`${t("colony.collect")} ${t(`res.${row.resource}`)} ${row.pending}`} onPointerDown={event => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); const rect = event.currentTarget.getBoundingClientRect(); void useColony.getState().collect(row.rowId, { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 }); }}><ResourceIcon resource={row.resource} size={30} /><span>+{formatCompact(row.pending)}</span></button>)}
            <button type="button" className="label-btn" aria-label={buildingName(b.buildingId)} onClick={() => select(b.id)}>
              <span className="label-level">{b.level}</span>
              {b.state !== "idle" && b.finishesAt ? (
                <span className="label-timer">
                  <FunctionalIcon name="clock" /> <Timer finishesAt={b.finishesAt} />
                </span>
              ) : b.canUpgrade.ok ? (
                <span className="label-ready" title={t("base.upgrade")}>
                  <FunctionalIcon name="arrowUp" />
                </span>
              ) : null}
            </button>
            {base.loop?.beacons.some((beacon) => beacon.rowId === b.id && beacon.kind !== "collect" && beacon.kind !== "upgrade") ? <span className="colony-beacon" aria-hidden="true"><FunctionalIcon name="dot" /></span> : null}
          </div>
        ))}
      {base.zones
        .filter((z) => z.state !== "open")
        .map((z) => (
          <div key={z.id} ref={setRef(`zone:${z.id}`)} className="label zone" data-testid={`zone-label-${z.id}`}>
            <button type="button" className="label-btn" onClick={() => useBase.getState().setPanel({ kind: "zones" })}>
              {z.state === "reclaiming" && z.finishesAt ? (
                <span className="label-timer">
                  <FunctionalIcon name="gather" /> <Timer finishesAt={z.finishesAt} />
                </span>
              ) : (
                <span className="label-lock"><FunctionalIcon name="lock" /> HQ {z.unlockHq}</span>
              )}
            </button>
          </div>
        ))}
    </div>
  );
}
